import { Link } from 'react-router-dom';
import { FaBriefcase, FaGraduationCap, FaCalendarAlt, FaExternalLinkAlt, FaRocket, FaCode, FaLaptopCode } from 'react-icons/fa';
import AnimatedIcon from './AnimatedIcon';
import AnimatedCard from './AnimatedCard';

const Experience = () => {
  const workExperience = [
    {
      title: 'PHP Full Stack Developer Trainee',
      company: 'Ezeeinfo cloud solutions',
      period: '2023 - PRESENT',
      description: [
        'Developed and maintained internal web applications to manage parcel tracking, invoicing, and scheduling using PHP',
        'Integrated RESTful APIs for third-party shipping partners and payment gateways',
        'Created dynamic dashboards for admin and client portals to improve transparency in parcel status and operations',
        'Assisted in the development of backend modules for parcel tracking and route optimization systems'
      ]
    },
    {
      title: 'PHP Version Migration Project',
      company: 'Project Work',
      period: '2025',
      description: [
        'Led the successful migration of an entire legacy system to the latest stable version of PHP, ensuring compatibility with updated libraries and frameworks',
        'Conducted a thorough audit of existing codebase to identify deprecated functions and optimize performance',
        'Collaborated with web development teams to debug and ensure smooth transition, reducing system downtime during migration'
      ]
    }
  ];

  const projects = [
    {
      title: 'Gym Management Application',
      description: 'Designed and developed a full stack Gym Management Application using Node.js, React, and MongoDB, enabling user registration, workout scheduling, trainer management, and real-time progress tracking.'
    },
    {
      title: 'BMI Calculator & Fitness Tracker',
      description: 'Built a comprehensive BMI calculator with calorie tracking, fitness recommendations, and integration with Rugged Fitness Coach Sabari YouTube channel for workout guidance and nutrition tips.'
    }
  ];

  const education = {
    degree: 'Bachelor of Science | Computer Science',
    institution: 'Government Arts College of Men\'s',
    period: '2018-2021'
  };

  return (
    <section className="bg-gradient-to-b from-[#0e0e2c] via-[#2d1b3d] to-[#1f1f1f] text-white py-16 px-6 border-b-4 border-pink-500/30">
      <div className="max-w-6xl mx-auto">
        {/* Section Header with Download Button */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-12">
          <div className="text-center md:text-left mb-4 md:mb-0">
            <AnimatedIcon 
              icon={FaRocket}
              size={32}
              color="text-pink-500"
              animation="bounce"
              delay={100}
              className="mx-auto md:mx-0 mb-4"
            />
            <p className="text-pink-500 font-medium italic mb-2 animate-fade-in">My Journey</p>
            <h2 className="text-2xl md:text-3xl font-bold animate-fade-in-up">Experience & Education</h2>
          </div>
        </div>
        {/* Work Experience */}
        <div className="mb-16">
          <div className="flex items-center mb-8">
            <AnimatedIcon 
              icon={FaBriefcase}
              size={28}
              color="text-pink-500"
              animation="glow"
              delay={200}
              className="mr-3"
            />
            <h2 className="text-2xl md:text-3xl font-bold animate-fade-in-up">Work Experience</h2>
          </div>
          
          <div className="space-y-8">
            {workExperience.map((job, index) => (
              <AnimatedCard
                key={index}
                delay={index * 100 + 300}
                animation="fadeInUp"
                hoverEffect="lift"
                className="border-l-4 border-pink-500 pl-6 relative bg-gradient-to-r from-purple-900/40 via-pink-900/30 to-indigo-900/40 p-6 rounded-r-xl backdrop-blur-sm border border-pink-500/20"
              >
                <div className="absolute w-4 h-4 bg-pink-500 rounded-full -left-2 top-8 animate-pulse"></div>
                <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-2">
                  <h3 className="text-xl font-semibold text-yellow-400 flex items-center">
                    <AnimatedIcon 
                      icon={FaCode}
                      size={20}
                      color="text-yellow-400"
                      animation="float"
                      delay={index * 50 + 400}
                      className="mr-2"
                    />
                    {job.title}
                  </h3>
                  <span className="text-pink-400 font-medium flex items-center bg-pink-500/10 px-3 py-1 rounded-full">
                    <FaCalendarAlt className="mr-2" size={14} />
                    {job.period}
                  </span>
                </div>
                <div className="text-gray-300 font-medium mb-4 flex items-center">
                  <AnimatedIcon 
                    icon={FaLaptopCode}
                    size={16}
                    color="text-gray-400"
                    animation="pulse"
                    delay={index * 50 + 450}
                    className="mr-2"
                  />
                  {job.company}
                </div>
                <ul className="text-gray-300 text-sm space-y-3">
                  {job.description.map((item, idx) => (
                    <li key={idx} className="flex items-start hover:text-white transition-colors group">
                      <span className="text-pink-500 mr-3 mt-1 group-hover:animate-bounce">•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </AnimatedCard>
            ))}
          </div>
        </div>

        {/* Projects */}
        <div className="mb-16">
          <h3 className="text-xl font-bold text-yellow-400 mb-6 flex items-center">
            <AnimatedIcon 
              icon={FaRocket}
              size={24}
              color="text-yellow-400"
              animation="shake"
              delay={600}
              className="mr-3"
            />
            Own Projects Developed
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {projects.map((project, index) => (
              <AnimatedCard
                key={index}
                delay={index * 150 + 700}
                animation="scaleIn"
                hoverEffect="lift"
                glowColor="purple"
                className="bg-gradient-to-br from-purple-900/60 via-pink-900/50 to-indigo-900/60 p-6 rounded-xl backdrop-blur-sm border border-purple-500/40"
              >
                <div className="flex justify-between items-start mb-4">
                  <h4 className="font-semibold text-white text-lg flex items-center">
                    <AnimatedIcon 
                      icon={FaCode}
                      size={20}
                      color="text-purple-400"
                      animation="rotate"
                      delay={index * 100 + 800}
                      className="mr-2"
                    />
                    {project.title}
                  </h4>
                  {project.title.includes('Gym Management') && (
                    <Link
                      to="/gym-management"
                      className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center space-x-2 animate-card-hover"
                    >
                      <FaExternalLinkAlt />
                      <span>Live Demo</span>
                    </Link>
                  )}
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">{project.description}</p>
              </AnimatedCard>
            ))}
          </div>
        </div>

        {/* Education */}
        <div>
          <div className="flex items-center mb-6">
            <AnimatedIcon 
              icon={FaGraduationCap}
              size={28}
              color="text-pink-500"
              animation="bounce"
              delay={1000}
              className="mr-3"
            />
            <h2 className="text-2xl md:text-3xl font-bold animate-fade-in-up">Education</h2>
          </div>
          
          <AnimatedCard
            delay={1100}
            animation="fadeInUp"
            hoverEffect="glow"
            glowColor="pink"
            className="border-l-4 border-pink-500 pl-6 relative bg-gradient-to-r from-purple-900/40 via-pink-900/30 to-indigo-900/40 p-6 rounded-r-xl backdrop-blur-sm border border-pink-500/20"
          >
            <div className="absolute w-4 h-4 bg-pink-500 rounded-full -left-2 top-8 animate-pulse"></div>
            <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-2">
              <h3 className="text-xl font-semibold text-yellow-400 flex items-center">
                <AnimatedIcon 
                  icon={FaGraduationCap}
                  size={20}
                  color="text-yellow-400"
                  animation="float"
                  delay={1200}
                  className="mr-2"
                />
                {education.degree}
              </h3>
              <span className="text-pink-400 font-medium flex items-center bg-pink-500/10 px-3 py-1 rounded-full">
                <FaCalendarAlt className="mr-2" size={14} />
                {education.period}
              </span>
            </div>
            <p className="text-gray-300 font-medium">{education.institution}</p>
          </AnimatedCard>
        </div>
      </div>
    </section>
  );
};

export default Experience;